/**
 * trimMetaDescription(text, maxLen=160)
 * ----------------------------------------------------------------------
 * Trim a meta description to a word boundary ≤ maxLen chars, with
 * no trailing punctuation stub. Used by every path that generates a
 * meta description on the fly (blog detail, program auto-SEO, etc.)
 * so no bot ever indexes a mid-word cut like
 *   "...want to be part of a big industr"
 * (real production example from the 2026-02 T8 audit).
 *
 * Rules:
 *   • If text ≤ maxLen → return as-is (trimmed of outer whitespace).
 *   • Otherwise, slice to maxLen, then back up to the last ASCII
 *     whitespace. Strip trailing commas, hyphens, and connectors so
 *     the string ends on a word, not a dangling comma.
 *   • Never return more than maxLen chars.
 *   • Preserve the full first sentence when the sentence itself is ≤
 *     maxLen (so a 120-char sentence stays intact instead of being
 *     awkwardly extended with partial words from the next sentence).
 */
export function trimMetaDescription(text, maxLen = 160) {
  if (!text) return "";
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (clean.length <= maxLen) return clean;

  // If the first sentence fits within maxLen, prefer it.
  const sentenceEnd = clean.search(/[.!?](?=\s|$)/);
  if (sentenceEnd !== -1 && sentenceEnd + 1 <= maxLen) {
    return clean.slice(0, sentenceEnd + 1);
  }

  // Otherwise, slice to maxLen and back up to the previous whitespace.
  let slice = clean.slice(0, maxLen);
  const lastSpace = slice.lastIndexOf(" ");
  if (lastSpace > 0) slice = slice.slice(0, lastSpace);
  // Strip trailing junk ("," "-" ":" ";" "(" "/" "\\" "&" and whitespace).
  slice = slice.replace(/[\s,\-:;(\/\\&]+$/, "");
  return slice;
}

export default trimMetaDescription;
